import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import LoginForm from './components/LoginForm'
import Dashboard from './components/Dashboard'
import ShipmentDetails from './components/ShipmentDetails'
import PublicToolsInterface from './components/PublicToolsInterface'
import BusinessInfoSetup from './components/BusinessInfoSetup'
import StaffLogin from './components/StaffLogin'
import StaffDashboard from './components/StaffDashboard'
import StaffShipmentDetails from './components/StaffShipmentDetails'
// import CUSMADebugPage from './components/CUSMADebugPage' // Hidden - CUSMA form filling no longer needed
import ManualInvoiceGenerator from './components/ManualInvoiceGenerator'
import StaffManualInvoiceGenerator from './components/StaffManualInvoiceGenerator'
import ThreePLOrders from './components/ThreePLOrders'
import { apiService } from './services/apiService'
import posthog from './posthog'
import type { User } from './types'

// Staff Shipment Details Wrapper Component
function StaffShipmentDetailsWrapper({ token, onLogout }: { token: string, onLogout: () => void }) {
  const { clientId, shipmentId } = useParams()
  const navigate = useNavigate()
  const [shipment, setShipment] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadShipment = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/api/staff/clients/${clientId}/shipments/${shipmentId}/details`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        const data = await response.json()
        
        if (data.success) {
          setShipment(data.data)
        } else {
          setError(data.error || 'Failed to load shipment details')
        }
      } catch (error) {
        console.error('Error loading shipment details:', error)
        setError('Failed to load shipment details')
      } finally {
        setLoading(false)
      }
    }

    if (clientId && shipmentId) {
      loadShipment()
    }
  }, [clientId, shipmentId, token])

  const handleBack = () => {
    navigate(`/staff/clients/${clientId}`)
  }

  if (loading) {
    return (
      <div className="loading">
        <div>Loading shipment details...</div>
      </div>
    )
  }

  if (error || !shipment) {
    return (
      <div className="card">
        <button className="button secondary mb-4" onClick={handleBack}>
          ← Back to Shipments
        </button>
        <div className="error">
          {error || 'Shipment not found'}
        </div>
      </div>
    )
  }

  return (
    <StaffShipmentDetails
      shipment={shipment}
      clientId={parseInt(clientId!)}
      token={token}
      onBack={handleBack}
    />
  )
}

// Login Page Component
function LoginPage() {
  const { login, loading, error, clearError } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogin = async (email: string) => {
    clearError()
    const success = await login(email)
    if (success) {
      // Redirect to the page user was trying to access, or dashboard
      const from = location.state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
        </div>
      </header>
      
      <main className="container">
        {error && (
          <div className="error">
            {error}
          </div>
        )}
        
        <PublicToolsInterface onLogin={handleLogin} loading={loading} />
      </main>
    </div>
  )
}

// Dashboard Page Component
function DashboardPage() {
  const { user, logout, loading } = useAuth()
  const [showBusinessSetup, setShowBusinessSetup] = useState(false)

  // Show loading while auth is being determined
  if (loading) {
    return (
      <div className="app">
        <header className="header">
          <div className="container">
            <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
          </div>
        </header>
        <main className="container">
          <div className="loading">
            <div>Loading...</div>
          </div>
        </main>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <div className="flex justify-between items-center">
            <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
            <div className="flex items-center gap-4">
              <span>
                {user.companyName ? (
                  <>Welcome, {user.companyName} ({user.email})</>
                ) : (
                  <>Welcome, {user.email}</>
                )}
              </span>
              <button 
                className="button secondary"
                onClick={() => setShowBusinessSetup(true)}
                style={{ fontSize: '14px', padding: '6px 12px' }}
              >
                Edit Business Info
              </button>
              <button className="button secondary" onClick={logout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container">
        <Dashboard 
          user={user} 
          onBusinessInfoUpdate={() => setShowBusinessSetup(true)}
        />
      </main>

      {/* Business Info Setup Modal */}
      {showBusinessSetup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: '20px'
          }}>
            <BusinessInfoSetup
              onComplete={() => setShowBusinessSetup(false)}
              onSkip={() => setShowBusinessSetup(false)}
              editMode={true}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// Manual Invoice Generator Page Component
function ManualInvoiceGeneratorPage() {
  const { user, logout, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  // Show loading while auth is being determined
  if (authLoading) {
    return (
      <div className="app">
        <header className="header">
          <div className="container">
            <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
          </div>
        </header>
        <main className="container">
          <div className="loading">
            <div>Loading...</div>
          </div>
        </main>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <div className="flex justify-between items-center">
            <h1>YYZ CBP - Manual Invoice Generator</h1>
            <div className="flex items-center gap-4">
              <button 
                className="button secondary"
                onClick={() => navigate('/dashboard')}
                style={{ fontSize: '14px', padding: '6px 12px' }}
              >
                ← Back to Dashboard
              </button>
              <span>
                {user.companyName ? (
                  <>Welcome, {user.companyName} ({user.email})</>
                ) : (
                  <>Welcome, {user.email}</>
                )}
              </span>
              <button className="button secondary" onClick={logout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container">
        <ManualInvoiceGenerator />
      </main>
    </div>
  )
}

// Shipment Details Page Component  
function ShipmentDetailsPage() {
  const { user, loading: authLoading } = useAuth()
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const [shipment, setShipment] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadShipmentForDetails = async () => {
      if (!shipmentId) return

      try {
        setLoading(true)
        setError(null)

        // OPTIMIZED: Direct shipment fetch instead of loading all shipments
        const response = await apiService.getShipment(shipmentId)
        if (response.success && response.data) {
          setShipment(response.data)
        } else {
          setError('Shipment not found')
        }
      } catch (err) {
        console.error('Failed to load shipment:', err)
        setError('Failed to load shipment')
      } finally {
        setLoading(false)
      }
    }

    loadShipmentForDetails()
  }, [shipmentId])

  const handleBackToList = () => {
    navigate('/dashboard')
  }

  // Show loading while auth is being determined
  if (authLoading) {
    return (
      <div className="app">
        <header className="header">
          <div className="container">
            <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
          </div>
        </header>
        <main className="container">
          <div className="loading">
            <div>Loading...</div>
          </div>
        </main>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (loading) {
    return (
      <div className="app">
        <header className="header">
          <div className="container">
            <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
          </div>
        </header>
        <main className="container">
          <div className="loading">
            <div>Loading shipment details...</div>
          </div>
        </main>
      </div>
    )
  }

  if (error || !shipment) {
    return (
      <div className="app">
        <header className="header">
          <div className="container">
            <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
          </div>
        </header>
        <main className="container">
          <div className="card">
            <button className="button secondary mb-4" onClick={handleBackToList}>
              ← Back to Dashboard
            </button>
            <div className="error">
              {error || 'Shipment not found'}
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <h1>YYZ CBP - HTS Classifier & Brokerage Fee Tools</h1>
        </div>
      </header>
      <main className="container">
        <ShipmentDetails 
          shipment={shipment} 
          onBack={handleBackToList}
        />
      </main>
    </div>
  )
}

// Staff Portal Components
function StaffPortal() {
  const location = useLocation()
  const navigate = useNavigate()
  const [staffUser, setStaffUser] = useState<any>(null)
  const [staffToken, setStaffToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Check for staff authentication
    const checkStaffAuth = async () => {
      try {
        const storedToken = localStorage.getItem('yyz-cbp-token')
        const storedUser = localStorage.getItem('staffUser')
        
        if (storedToken && storedUser) {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:2999'}/api/auth/staff-session`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          })
          const data = await response.json().catch(() => null)
          if (!response.ok || !data?.success) throw new Error(data?.error || 'Staff session expired')
          const validatedUser = { ...JSON.parse(storedUser), ...data.data }
          localStorage.setItem('staffUser', JSON.stringify(validatedUser))
          setStaffToken(storedToken)
          setStaffUser(validatedUser)
        }
      } catch (err) {
        console.error('Staff auth check failed:', err)
        localStorage.removeItem('yyz-cbp-token')
        localStorage.removeItem('staffUser')
      } finally {
        setLoading(false)
      }
    }

    checkStaffAuth()
  }, [])

  const handleStaffLogin = (token: string, user: any) => {
    setStaffToken(token)
    setStaffUser(user)
    setError(null)
  }

  const handleStaffLogout = () => {
    localStorage.removeItem('yyz-cbp-token')
    localStorage.removeItem('staffUser')
    localStorage.removeItem('yyz-cbp-staff-client-id')
    setStaffToken(null)
    setStaffUser(null)
  }

  if (loading) {
    return (
      <div className="loading">
        <div>Loading staff portal...</div>
      </div>
    )
  }

  return (
    <div className="app">
      {error && (
        <div className="error" style={{ margin: '20px' }}>
          {error}
        </div>
      )}

      <Routes>
        <Route path="/staff" element={
          !staffUser ? (
            <StaffLogin 
              onLogin={handleStaffLogin}
              error={error}
              loading={loading}
            />
          ) : (
            <StaffDashboard
              token={staffToken!}
              onLogout={handleStaffLogout}
            />
          )
        } />
        <Route path="/staff/clients/:clientId" element={
          !staffUser ? (
            <Navigate to="/staff" replace />
          ) : (
            <StaffDashboard
              token={staffToken!}
              onLogout={handleStaffLogout}
            />
          )
        } />
        <Route path="/staff/clients/:clientId/shipments/:shipmentId" element={
          !staffUser ? (
            <Navigate to="/staff" replace />
          ) : (
            <StaffShipmentDetailsWrapper
              token={staffToken!}
              onLogout={handleStaffLogout}
            />
          )
        } />
        {/* CUSMA Debug route removed - form filling no longer needed */}
        <Route path="/staff/manual-invoice" element={
          !staffUser ? (
            <Navigate to="/staff" replace />
          ) : (
            <StaffManualInvoiceGenerator
              token={staffToken!}
              onBack={() => navigate('/staff')}
            />
          )
        } />
        <Route path="/3pl" element={
          !staffUser ? (
            <Navigate to="/staff" replace />
          ) : (
            <ThreePLOrders token={staffToken!} onLogout={handleStaffLogout} />
          )
        } />
        <Route path="*" element={<Navigate to="/staff" replace />} />
      </Routes>
    </div>
  )
}

// Main App Content with Auth Integration
function AppContent() {
  const location = useLocation()
  const { logout } = useAuth()

  useEffect(() => {
    // Set up API service auth failure callback
    apiService.setAuthFailureCallback(() => {
      logout()
      // Force navigation to login if we're not already on public pages
      if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/staff')) {
        window.location.href = '/login'
      }
    })

    // Capture pageview on app load
    posthog.capture('$pageview')
  }, [logout, location])

  // Check if we're in staff mode or 3PL mode
  if (location.pathname.startsWith('/staff') || location.pathname.startsWith('/3pl')) {
    return <StaffPortal />
  }

  // Regular user routes
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/shipment/:shipmentId" element={
        <ProtectedRoute>
          <ShipmentDetailsPage />
        </ProtectedRoute>
      } />
      <Route path="/manual-invoice" element={
        <ProtectedRoute>
          <ManualInvoiceGeneratorPage />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
